import { useContext, useMemo, useState } from 'react';
import axios from '../config/axios';
import { useQuery } from '@tanstack/react-query';
import Loading from '../components/Loading';
import { useAuth } from 'react-oidc-context';
import CartOrderSummary from '../components/OrderSummary';
import ShippingDetails from '../components/ShippingDetails';
import {
  CartApiItems,
  CartDetailType,
  PrescriptionStatus,
  ShopifyCustomerAddress,
} from '../interface/interface';
import LoadingButton from '../components/LoadingButton';
import { CartContext } from '../context/cartContext';
import { calculateOutOfStockEnum } from '../utils/outOfStockStatus';
import { emptyCartCall } from '../service';
import { EmptyCartIcon } from '../assets/svgImages';
import showToast from '../components/ToastComponent';
import {
  CartModalStorageKeys,
  productNameToExclude,
} from '../constant/constant';
import { useNavigate } from 'react-router-dom';
import { useModalDismissal } from '../hooks/useModalDismissal';
import { PromotionalModal } from '../components/promotionalModal';
import { callCustomerAddresses } from '../service/callCustomerAddresses';
import { nativeService } from '../helpers/nativeService';
import Breadcrumbs from '../components/Breadcrumbs';

export const Cart = () => {
  const { user } = useAuth();
  const { cart, setCart } = useContext(CartContext);
  const navigate = useNavigate();
  const { PermanentlyDismissed, TemporarilyDismissed } = CartModalStorageKeys;
  const [pageRefresh, setPageRefresh] = useState<boolean>(false);
  const [shippingAmountLoading, setShippingAmountLoading] =
    useState<boolean>(false);
  const [payoutLoading, setPayoutLoading] = useState<boolean>(false);
  const [shippingEditDetailEnabled, setEditDetailEnabled] =
    useState<boolean>(false);
  const [shippingConfirmChecked, setShippingConfirmChecked] =
    useState<boolean>(false);
  const [subConfirmReqd, setSubConfirmReqd] = useState<boolean>(false);
  const [subConfirmChecked, setSubConfirmChecked] = useState<boolean>(false);
  const [addressData, setAddressData] = useState<Array<ShopifyCustomerAddress>>(
    [],
  );
  const [hasNonActivePrescribedProducts, setHasNonActivePrescribedProducts] =
    useState<boolean>(false);

  const callCart = useMemo(
    () => async () => {
      try {
        setSubConfirmReqd(false);
        const token = await user?.access_token;

        const response = await axios<CartDetailType>('/api/v1/cart', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const cartResponse = response?.data;
        const products = await callProducts();
        const accessories = await callAccessories();
        products.push(...accessories);
        const lineItems: CartApiItems[] = cartResponse?.line_items?.map(
          (item: CartApiItems) => {
            const relatedProduct: any | undefined = products?.find(
              (products) => products.product_code === item.product_id,
            );
            if (!relatedProduct) {
              const relatedProductSubstitute: any | undefined = products?.find(
                (product) =>
                  product?.substituteProduct?.product_code === item.product_id,
              );
              if (relatedProductSubstitute) {
                setSubConfirmReqd(true);
              }
              return {
                ...item,
                substituteForId: relatedProductSubstitute?.product_code,
                max_quantity: relatedProductSubstitute?.productMaxQty,
                outOfStock: calculateOutOfStockEnum(
                  relatedProductSubstitute?.substituteProduct,
                  item.quantity,
                ),
              };
            }
            return {
              ...item,
              max_quantity: relatedProduct?.productMaxQty,
              outOfStock: calculateOutOfStockEnum(
                relatedProduct,
                item.quantity,
              ),
            };
          },
        );
        const cartData: CartDetailType = {
          ...cartResponse,
          line_items: lineItems,
        };

        const cart =
          lineItems?.length === 1 &&
          productNameToExclude.includes(lineItems[0].title);

        setHasNonActivePrescribedProducts(() =>
          Boolean(
            lineItems?.find(
              (e) =>
                (e.requiresPrescription &&
                  e?.prescription.status !== PrescriptionStatus.Active) ||
                e.prelimChecksError,
            ),
          ),
        );

        if (cart) {
          await handleEmptyCart();
          setCart(undefined);
          setEditDetailEnabled(!cartData?.shipping_address?.first_name);
          return true;
        }

        setCart(cartData);
        setEditDetailEnabled(!cartData?.shipping_address?.first_name);
        return true;
      } catch (error: any) {
        console.error(error);
        showToast({
          message: 'Unable to fetch updated cart!',
          type: 'error',
        });
        return false;
      }
    },
    [],
  );

  const { isFetching: cartDataLoading } = useQuery({
    queryKey: ['cartList'],
    queryFn: callCart,
  });

  const {
    isModalOpen,
    dontShowAgain,
    setDontShowAgain,
    dismissModal,
    closeModal,
  } = useModalDismissal({
    permanentlyDismissedKey: PermanentlyDismissed,
    temporarilyDismissedKey: TemporarilyDismissed,
    isLoading: cartDataLoading,
    cartLineItems: cart?.line_items,
  });

  const handleExploreNow = () => {
    dismissModal(dontShowAgain);
    navigate('/non-script-range');
  };

  const handleNoThanks = () => {
    dismissModal(dontShowAgain);
  };

  const handleCloseModal = () => {
    closeModal();
  };

  async function addProductToCart(id: string, substituteForId?: string) {
    try {
      const data = {
        product_id: id,
        substituteForId,
      };

      const res = await axios.post('/api/v1/cart/add', data, {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        validateStatus: (status) => {
          return status < 500;
        },
      });

      if (res.status === 200) {
        showToast({
          message: 'Item added to your cart',
          type: 'success',
        });
        await callCart();
      } else {
        showToast({
          message: res.data.message,
          type: 'warning',
          timeout: 6000,
        });
      }
    } catch (error) {
      console.error(error);
      showToast({
        message: 'Failed to add item',
        type: 'error',
      });
    }
  }

  async function removeProductFromCart(id: string) {
    try {
      const data = {
        product_id: id,
      };

      const res = await axios.post('/api/v1/cart/remove', data, {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        validateStatus: (status) => {
          return status < 500;
        },
      });

      if (res.status === 200) {
        showToast({
          message: 'Item removed from your cart.',
          type: 'success',
        });
        await callCart();
      } else {
        showToast({
          message: res.data.message,
          type: 'warning',
        });
      }
    } catch (error) {
      console.error(error);
      showToast({
        message: 'Unable to remove item',
        type: 'error',
      });
    }
  }

  const callProducts = async () => {
    try {
      const response = await axios<any[]>('/api/v1/products', {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });
      const products = response?.data;
      return products;
    } catch (error) {
      showToast({
        message: 'Failed to get Products',
        type: 'error',
      });
      return [];
    }
  };

  const callAccessories = async () => {
    try {
      const response = await axios<any[]>({
        url: '/api/v1/accessories',
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        method: 'GET',
      });

      return response?.data;
    } catch (err) {
      showToast({
        message: 'Failed to get Accessories',
        type: 'error',
      });
      return [];
    }
  };

  const callCustomerAddressesList = async () => {
    try {
      if (!user?.access_token) return;
      const addressList = await callCustomerAddresses(user?.access_token);
      setAddressData(addressList);
      return true;
    } catch (error) {
      setAddressData([]);
      return false;
    }
  };

  const { isFetching: isAddressListFetching } = useQuery({
    queryKey: ['addressList'],
    queryFn: async () => {
      await callCustomerAddressesList();
    },
  });

  const handleEmptyCart = async () => {
    if (!user?.access_token) return;
    setPageRefresh(true);
    const status = await emptyCartCall(user.access_token);
    if (status === 200) {
      showToast({
        message: 'Your cart has been emptied.',
        type: 'success',
      });
      setShippingAmountLoading(true);
      await callCart();
      setShippingAmountLoading(false);
      setPageRefresh(false);
      return;
    }

    setPageRefresh(false);
    showToast({
      message: 'Unable to clear cart',
      type: 'info',
    });
  };

  const addShipping = useMemo(
    () => async (shippingDetails: ShopifyCustomerAddress) => {
      const token = user?.access_token;
      await axios.post('/api/v1/cart/shipping', shippingDetails, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setShippingAmountLoading(true);
      await callCart();
      setShippingAmountLoading(false);
      return true;
    },
    [],
  );

  const proceedToPay = useMemo(
    () => async () => {
      setPayoutLoading(true);
      const token = user?.access_token;
      const urlResponse = await axios.get<string>('/api/v1/cart/payurl', {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Impersonated-Authorization': localStorage.getItem(
            'impersonateUser',
          )
            ? `Bearer ${
                JSON.parse(localStorage.getItem('impersonateUser') as string)
                  .access_token
              }`
            : undefined,
        },
      });
      const payUrl = urlResponse.data;
      if (typeof payUrl === 'object') {
        setPayoutLoading(false);
        showToast({
          message: (payUrl as { message: string }).message,
          type: 'info',
        });
      } else {
        setPayoutLoading(false);
        const nativeWebView = (window as any)?.ReactNativeWebView;
        const hasWebView = !!nativeWebView;
        if (hasWebView) {
          nativeService(JSON.stringify({ payUrl }));
        } else {
          window.open(payUrl, '_self');
        }
      }
    },
    [],
  );

  const enableEditShipping = useMemo(
    () => () => {
      setEditDetailEnabled(!shippingEditDetailEnabled);
    },
    [shippingEditDetailEnabled],
  );

  const loadedData = (
    <div className="pb-4">
      <div className="flex justify-between mt-2 mb-5 items-center">
        <h2 className="text-base xl:text-xl text-primary-blue-500 font-semibold">
          Order Summary
        </h2>
        <div>
          <button
            className="border border-primary-blue-500 rounded-md flex bg-white text-sm font-semibold text-primary-blue-500 px-5 py-2 items-center"
            onClick={() => handleEmptyCart()}
          >
            <span className="mr-2">
              <EmptyCartIcon className="w-6 h-6" />
            </span>
            Empty Cart
          </button>
        </div>
      </div>
      <CartOrderSummary
        cartData={cart!}
        shippingLoading={shippingAmountLoading}
        addProductToCart={addProductToCart}
        removeProductFromCart={removeProductFromCart}
      />

      <h2 className="text-base xl:text-xl text-primary-blue-500 font-semibold mt-10 mb-3">
        Shipping Details
      </h2>
      <ShippingDetails
        cart={cart}
        addressList={addressData}
        isAddressListFetching={isAddressListFetching}
        addShipping={addShipping}
        editShippingEnabled={shippingEditDetailEnabled}
      />
      {!shippingEditDetailEnabled && (
        <>
          <div
            className="text-blue-500 mb-3 text-sm underline underline-offset-2 cursor-pointer"
            onClick={enableEditShipping}
          >
            {'Edit Shipping Details'}
          </div>
          <label className="flex items-start  space-x-3 mb-2">
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-blue-600 mt-1"
              checked={shippingConfirmChecked}
              onChange={(e) => setShippingConfirmChecked(e.target.checked)}
            />
            <span className="font-normal text-lg">
              I confirm that all information provided, including my shipping
              address, is accurate and intended for delivery to me. I
              acknowledge that incorrect details may result in delivery delays
              and additional charges
            </span>
          </label>
          {subConfirmReqd && (
            <label className="flex items-center space-x-3 mb-3">
              <input
                type="checkbox"
                className="form-checkbox h-4 w-4 text-blue-600"
                checked={subConfirmChecked}
                onChange={(e) => setSubConfirmChecked(e.target.checked)}
              />
              <span className="font-normal text-sm">
                I acknowledge that 1 or more prescribed products have been
                substituted with a product that a pharmacist has deemed
                equivalent and I consent to that substitution.
              </span>
            </label>
          )}
        </>
      )}
      {!!cart?.shipping_rate && !shippingEditDetailEnabled && (
        <div className="mt-7 w-full md:w-1/2 xl:w-1/4">
          <LoadingButton
            isLoading={payoutLoading}
            buttonText="Proceed to Pay"
            buttonIcon={<>{'💸'}</>}
            isDisabled={
              !shippingConfirmChecked ||
              !(!subConfirmReqd || subConfirmChecked) ||
              hasNonActivePrescribedProducts
            }
            onClick={async () => {
              await proceedToPay();
            }}
          />
        </div>
      )}
    </div>
  );

  const renderCartContent = () => {
    if (cartDataLoading || pageRefresh) return <Loading />;
    if (cart?.total_price) return loadedData;
    return <>Your Cart is Empty!</>;
  };

  return (
    <div className="container-standard">
      <Breadcrumbs
        data={[
          {
            tag: 'a',
            className: 'underline',
            href: '/home',
            children: <>Home</>,
          },
          {
            tag: 'a',
            href: '',
            children: <>My Cart</>,
          },
        ]}
      />
      <div className="result-block-container bg-white shadow px-5 2xl:px-10 py-5 rounded-md relative mb-10">
        <h1 className="text-xl xl:text-3xl text-primary-blue-500 font-semibold mb-3">
          My Cart
        </h1>
        <p className="text-xs text-primary-blue-500 font-regular lg:italic lg:absolute right-2 top-2 mb-5">
          Your cart will be automatically cleared after 10 days of inactivity.
        </p>
        {renderCartContent()}
        <PromotionalModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onExploreNow={handleExploreNow}
          onNoThanks={handleNoThanks}
          dontShowAgain={dontShowAgain}
          setDontShowAgain={setDontShowAgain}
        />
      </div>
    </div>
  );
};

export default Cart;
